import { useEffect, useState } from 'react';
import { Alert, App, Button, Card, InputNumber, Skeleton, Space, Typography } from 'antd';
import { PercentageOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';

interface AdminTdsRateView {
  tdsBps: number;
  tdsBpsNoPan: number;
  ratePercent: number;
  ratePercentNoPan: number;
}

/** Whole percents only, and never 0 — the same rule the API enforces
 *  (`TdsRateSchema` / `sanitizeTdsBps`). A payout request always writes a TDS
 *  ledger leg and the ledger refuses an amount of 0, so a 0% rate would make
 *  every listener withdrawal fail. */
export const TDS_LIMITS = { min: 1, max: 100 } as const;

function serverError(e: unknown): string {
  const data = (e as { response?: { data?: { message?: string } } }).response?.data;
  return data?.message ?? 'Could not save the TDS rate';
}

/**
 * Settings → TDS rate. What is withheld from a listener's payout when they
 * REQUEST it: one rate for a listener with a verified PAN on file, a higher
 * one for a listener without.
 *
 * Only ever affects NEW payout requests. Every existing request keeps the rate
 * and the amount it was created with — approving, marking paid and reversing
 * all work from that snapshot, so changing this never moves money that has
 * already been accounted for.
 */
export function TdsRateCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('tds_rate.edit');
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'tds-rate'],
    queryFn: async () => (await api.get<AdminTdsRateView>('/admin/payouts/tds-rate')).data,
  });
  const [withPan, setWithPan] = useState<number | null>(null);
  const [noPan, setNoPan] = useState<number | null>(null);

  // Seed the inputs from the server, and re-seed whenever the server value
  // itself changes (a refetch after another admin edited the rate) — but never
  // while this operator has unsaved edits of their own.
  const [seeded, setSeeded] = useState<{ withPan: number; noPan: number } | null>(null);
  useEffect(() => {
    if (!data) return;
    const next = { withPan: data.tdsBps / 100, noPan: data.tdsBpsNoPan / 100 };
    const unchanged =
      seeded !== null && seeded.withPan === next.withPan && seeded.noPan === next.noPan;
    if (unchanged) return;
    const dirty =
      seeded !== null && (withPan !== seeded.withPan || noPan !== seeded.noPan);
    setSeeded(next);
    if (!dirty) {
      setWithPan(next.withPan);
      setNoPan(next.noPan);
    }
  }, [data, seeded, withPan, noPan]);

  const save = useMutation({
    mutationFn: async (next: { tdsBps: number; tdsBpsNoPan: number }) =>
      (await api.patch<AdminTdsRateView>('/admin/payouts/tds-rate', next)).data,
    onSuccess: (saved) => {
      message.success(
        `TDS set to ${saved.ratePercent}% with PAN, ${saved.ratePercentNoPan}% without`,
      );
      qc.setQueryData(['admin', 'tds-rate'], saved);
      // Another admin may have changed these meanwhile; take the server's word.
      void qc.invalidateQueries({ queryKey: ['admin', 'tds-rate'] });
      setWithPan(saved.tdsBps / 100);
      setNoPan(saved.tdsBpsNoPan / 100);
    },
    onError: (e) => message.error(serverError(e)),
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 2 }} />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load the TDS rate"
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => void refetch()}>
            Retry
          </Button>
        }
      />
    );
  }
  const ok = (v: number | null): v is number =>
    v !== null && Number.isInteger(v) && v >= TDS_LIMITS.min && v <= TDS_LIMITS.max;
  const valid = ok(withPan) && ok(noPan);
  const dirty = withPan !== data.tdsBps / 100 || noPan !== data.tdsBpsNoPan / 100;
  const required = (v: number | null) =>
    v === null ? 'Required — a rate of 0% is not allowed' : null;

  return (
    <Card
      title={
        <Space>
          <PercentageOutlined />
          TDS on payouts
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 560 }}>
        <Alert
          type="info"
          showIcon
          message="Applies to new payout requests only"
          description="Payout requests that already exist keep the rate they were created with, including any still awaiting approval or payment. Confirm the statutory rate with finance before changing it."
        />
        <div>
          <Typography.Text>With a verified PAN (%)</Typography.Text>
          <div>
            <InputNumber
              data-testid="tds-rate-with-pan"
              aria-label="TDS with a verified PAN (percent)"
              min={TDS_LIMITS.min}
              max={TDS_LIMITS.max}
              precision={0}
              value={withPan}
              disabled={!canEdit}
              status={ok(withPan) ? undefined : 'error'}
              // An empty field stays empty: coercing it to 0 would store a
              // 0% rate, which breaks every payout.
              onChange={(v) => setWithPan(v)}
            />
          </div>
          {required(withPan) && (
            <Typography.Text type="danger" data-testid="tds-rate-with-pan-error" style={{ fontSize: 12 }}>
              {required(withPan)}
            </Typography.Text>
          )}
        </div>
        <div>
          <Typography.Text>Without a verified PAN (%)</Typography.Text>
          <div>
            <InputNumber
              data-testid="tds-rate-no-pan"
              aria-label="TDS without a verified PAN (percent)"
              min={TDS_LIMITS.min}
              max={TDS_LIMITS.max}
              precision={0}
              value={noPan}
              disabled={!canEdit}
              status={ok(noPan) ? undefined : 'error'}
              onChange={(v) => setNoPan(v)}
            />
          </div>
          {required(noPan) && (
            <Typography.Text type="danger" data-testid="tds-rate-no-pan-error" style={{ fontSize: 12 }}>
              {required(noPan)}
            </Typography.Text>
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Whole percents only, {TDS_LIMITS.min}–{TDS_LIMITS.max} (0% is not allowed). The listener's app shows this rate
            and the net amount before they confirm a payout.
          </Typography.Text>
        </div>
        {canEdit && (
          <Space>
            <Button
              type="primary"
              data-testid="tds-rate-save"
              disabled={!dirty || !valid}
              loading={save.isPending}
              onClick={() =>
                ok(withPan) &&
                ok(noPan) &&
                save.mutate({ tdsBps: withPan * 100, tdsBpsNoPan: noPan * 100 })
              }
            >
              Save
            </Button>
            {dirty && (
              <Button
                onClick={() => {
                  setWithPan(data.tdsBps / 100);
                  setNoPan(data.tdsBpsNoPan / 100);
                }}
                disabled={save.isPending}
              >
                Discard
              </Button>
            )}
          </Space>
        )}
      </Space>
    </Card>
  );
}
